import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import useIsMobile from "../lib/useIsMobile";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Mail, Lock, CheckCircle2, Eye, EyeOff, Check } from "lucide-react";

// Logo do app. Usa o arquivo /sitesync-icon.png (o mesmo icone do app). Se a imagem
// nao abrir por qualquer motivo, mostra um quadrado com "SS" em vez do icone quebrado.
function LogoMark({ className = "h-20 w-20", rounded = "rounded-2xl" }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div
        className={`grid place-items-center bg-[#101827] font-bold text-primary ring-1 ring-white/10 ${className} ${rounded}`}
        aria-label="SiteSync"
      >
        SS
      </div>
    );
  }
  return (
    <img
      src="/sitesync-icon.png"
      alt="SiteSync"
      onError={() => setFailed(true)}
      className={`object-cover ring-1 ring-white/10 ${className} ${rounded}`}
    />
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const data = await login(email, password);
      // "Keep me signed in" desmarcado: o login vale so ate fechar o app ou a aba.
      // (O api.js confere estas marcas quando o app abre.)
      try {
        if (keepSignedIn) {
          localStorage.removeItem("ss_keep");
        } else {
          localStorage.setItem("ss_keep", "0");
          sessionStorage.setItem("ss_alive", "1");
        }
      } catch (storageErr) {
        // sem acesso ao armazenamento: segue normalmente
      }
      toast.success("Welcome back");
      const status = data?.company?.status;
      if (status && status !== "active" && !data?.user?.is_master_admin) {
        navigate("/app/pending");
      } else {
        navigate("/app");
      }
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail, "Login failed");
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  /* ---------- Celular: tela inteira, sem cartao ---------- */
  if (isMobile) {
    const field =
      "h-14 w-full rounded-xl border border-white/10 bg-[#13161c] text-base text-white placeholder:text-zinc-500 outline-none transition-colors focus:border-primary";
    return (
      <div
        className="relative flex min-h-screen flex-col bg-[#0a0b0f] text-white"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="mx-auto flex w-full max-w-md flex-1 flex-col px-6 pt-[calc(env(safe-area-inset-top)_+_3rem)]">
          {/* Logo e nome */}
          <div className="flex flex-col items-center">
            <LogoMark className="h-20 w-20" rounded="rounded-2xl" />
            <div className="mt-4 text-2xl font-semibold tracking-tight">SiteSync</div>
          </div>

          {/* Titulo */}
          <div className="mt-12">
            <h1 className="text-[2rem] font-bold leading-tight tracking-tight">Welcome back</h1>
            <p className="mt-2 text-base leading-snug text-zinc-400">
              Sign in to manage your projects, ITPs and site teams.
            </p>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <label htmlFor="email" className="mb-2 block text-base font-medium">
                Work email
              </label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-zinc-400" />
                <input
                  id="email"
                  type="email"
                  inputMode="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`${field} pl-12 pr-4`}
                  placeholder="name@company.com"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <label htmlFor="password" className="block text-base font-medium">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="py-1 text-base font-medium text-primary"
                  data-testid="login-forgot-link"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-zinc-400" />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${field} pl-12 pr-14`}
                  placeholder="Enter your password"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-1 top-1/2 grid h-12 w-12 -translate-y-1/2 place-items-center text-zinc-400"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Keep me signed in */}
            <button
              type="button"
              role="checkbox"
              aria-checked={keepSignedIn}
              onClick={() => setKeepSignedIn((v) => !v)}
              className="flex min-h-[44px] items-center gap-3 text-left"
              data-testid="login-keep-signed-in"
            >
              <span
                className={`grid h-6 w-6 shrink-0 place-items-center rounded-md border transition-colors ${
                  keepSignedIn ? "border-primary bg-primary text-white" : "border-white/25 bg-transparent"
                }`}
              >
                {keepSignedIn && <Check className="h-4 w-4" strokeWidth={3} />}
              </span>
              <span className="text-base">Keep me signed in</span>
            </button>

            {err && (
              <div
                className="rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300"
                data-testid="login-error"
              >
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="flex h-14 w-full items-center justify-center rounded-xl bg-primary text-lg font-semibold text-black transition-opacity active:opacity-90 disabled:opacity-60"
              data-testid="login-submit-button"
            >
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
            </button>
          </form>

          {/* Novo por aqui */}
          <div className="mt-8 flex items-center gap-4 text-base text-zinc-400">
            <span className="h-px flex-1 bg-white/10" />
            New to SiteSync?
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3">
            <Link
              to="/signup"
              className="flex h-14 items-center justify-center rounded-xl border border-white/15 bg-[#101318] px-2 text-center text-base font-medium active:bg-white/5"
              data-testid="login-signup-link"
            >
              Create company
            </Link>
            <Link
              to="/join"
              className="flex h-14 items-center justify-center rounded-xl border border-white/15 bg-[#101318] px-2 text-center text-base font-medium active:bg-white/5"
              data-testid="login-join-link"
            >
              Join with invite
            </Link>
          </div>

          {/* Rodape */}
          <div className="mt-auto flex items-center justify-center gap-3 pb-4 pt-10 text-sm text-zinc-500">
            <Link to="/terms" className="py-2">
              Terms
            </Link>
            <span>·</span>
            <Link to="/privacy" className="py-2">
              Privacy
            </Link>
            <span>·</span>
            <span>v1.0</span>
          </div>
        </div>
      </div>
    );
  }

  /* ---------- Computador: como era ---------- */
  return (
    <AuthShell>
      <div className="label-overline mb-3">SiteSync / Login</div>
      <h1 className="text-3xl font-semibold tracking-tighter mb-1">Sign in to your console.</h1>
      <p className="text-sm text-muted-foreground mb-8">Welcome back — enter your details to continue.</p>

      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div>
          <Label htmlFor="email" className="label-overline mb-2 block">Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-md font-mono pl-9 h-11"
              placeholder="you@company.com"
              data-testid="login-email-input"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="password" className="label-overline mb-2 block">Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-md font-mono pl-9 h-11"
              placeholder="••••••••"
              data-testid="login-password-input"
            />
          </div>
        </div>

        {err && (
          <div className="text-xs text-destructive font-mono border border-destructive/40 bg-destructive/5 px-3 py-2 rounded-md" data-testid="login-error">
            {err}
          </div>
        )}

        <Button
          type="submit"
          disabled={busy}
          className="w-full rounded-md h-11 bg-primary hover:bg-primary/90 font-medium transition-colors"
          data-testid="login-submit-button"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
        </Button>
      </form>

      <div className="mt-4 text-right">
        <Link
          to="/forgot-password"
          className="text-xs font-mono text-muted-foreground hover:text-primary transition-colors"
          data-testid="login-forgot-link"
        >
          Forgot password?
        </Link>
      </div>

      <div className="mt-8 border-t border-border pt-6 text-sm text-muted-foreground">
        No account?{" "}
        <Link to="/signup" className="text-foreground hover:text-primary font-medium transition-colors" data-testid="login-signup-link">
          Create company
        </Link>
        <span className="mx-2">·</span>
        <Link to="/join" className="text-foreground hover:text-primary font-medium transition-colors" data-testid="login-join-link">
          Join with invite
        </Link>
      </div>
    </AuthShell>
  );
}

const FEATURES = [
  "Real-time attendance with GPS geofencing",
  "Digital ITP execution with sign-off trails",
  "Jobsite-scoped chat, logs and rosters",
];

export function AuthShell({ children }) {
  return (
    <div className="relative flex min-h-screen flex-col bg-black md:grid md:grid-cols-2 md:bg-background">
      {/* Painel da marca. No celular fica no topo (logo + frase); no computador
          vira a coluna da esquerda. O topo respeita a area do notch do iPhone
          e ainda deixa uma folga, pra o logo nao ficar colado na barra de status. */}
      <div className="relative overflow-hidden bg-black text-white md:flex">
        <div className="absolute inset-0 grid-lines opacity-20" />
        <div
          className="absolute inset-0"
          style={{
            background: "radial-gradient(60% 50% at 20% 15%, rgba(249,115,22,0.18) 0%, rgba(0,0,0,0) 70%)",
          }}
        />
        <div className="relative px-6 pb-8 pt-[calc(env(safe-area-inset-top)_+_3.5rem)] text-center md:m-auto md:max-w-md md:p-10 md:text-left">
          <div className="mb-10 flex items-center justify-center gap-3 md:mb-12 md:justify-start">
            <LogoMark className="h-14 w-14 shrink-0" rounded="rounded-xl" />
            <div className="text-left">
              <div className="text-lg font-semibold tracking-tight text-white">SiteSync</div>
              <div className="label-overline text-zinc-400">Web Console</div>
            </div>
          </div>

          <div className="text-[2.25rem] font-semibold leading-[1.05] tracking-tighter text-white md:text-4xl md:leading-tight">
            The command
            <br className="hidden md:block" /> surface for
            <br className="hidden md:block" /> <span className="text-primary">every jobsite.</span>
          </div>

          <ul className="mt-10 hidden space-y-3 md:block">
            {FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-sm text-zinc-300">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 hidden border-t border-white/10 pt-6 font-mono text-xs uppercase tracking-widest text-zinc-500 md:block">
            Built for construction teams
          </div>
        </div>
      </div>

      {/* Painel do formulario */}
      <div className="flex flex-1 flex-col bg-transparent px-6 pb-[calc(env(safe-area-inset-bottom)_+_1.5rem)] pt-2 md:flex-row md:items-center md:justify-center md:bg-background md:p-12">
        <div className="mx-auto flex w-full max-w-md flex-1 flex-col md:flex-none">
          <div className="rounded-lg border border-border bg-card p-6 text-card-foreground shadow-sm md:p-8">{children}</div>

          <div className="mt-auto pt-10 text-center font-mono text-[11px] uppercase tracking-widest text-zinc-500 md:hidden">
            Built for construction teams
          </div>
        </div>
      </div>
    </div>
  );
}
